
import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AppProvider } from "./contexts/AppContext";
import Layout from "./components/layout/Layout";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import SettingsPage from './components/settings/SettingsPage';

// Pages
import HomePage from "./pages/HomePage";
import Index from "./pages/Index"; 
import TablesPage from "./pages/TablesPage";
import TableDetailPage from "./pages/TableDetailPage";
import KitchenPage from "./pages/KitchenPage";
import DeliveryPage from "./pages/DeliveryPage";
import LoginPage from "./pages/LoginPage";
import NotFound from "./pages/NotFound";
import CompletedTablesPage from "./pages/CompletedTablesPage";
import CashierClosingPage from "./pages/CashierClosingPage";
import PDVPage from "./pages/PDVPage";

// Determine if we're in production using consistent logic
const productionDomains = ['primeconveniencia.com.br', 'rspcloud.com.br'];
const isProduction = productionDomains.some(domain => window.location.hostname.includes(domain));
const basePath = isProduction ? '/mobile' : '';

console.log('App.tsx - Inicializando com basePath:', basePath, 'isProduction:', isProduction);

// Create a new QueryClient instance
const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter basename={basePath}>
          <AppProvider>
            <Toaster />
            <Sonner />
            <Routes>
              {/* Rota de login - acessível sem autenticação */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Rota principal - acessível a todos os usuários */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout>
                    <Index />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Rota home alternativa */}
              <Route path="/home" element={
                <ProtectedRoute>
                  <Layout>
                    <HomePage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Rotas de mesas - apenas admin e garçom */}
              <Route path="/tables" element={
                <ProtectedRoute allowedRoles={['admin', 'waiter']}>
                  <Layout>
                    <TablesPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/tables/:id" element={
                <ProtectedRoute allowedRoles={['admin', 'waiter']}>
                  <Layout>
                    <TableDetailPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Rota para mesas finalizadas */}
              <Route path="/completed-tables" element={
                <ProtectedRoute allowedRoles={['admin', 'waiter']}>
                  <Layout>
                    <CompletedTablesPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Nova rota para fechamento de caixa */}
              <Route path="/cashier-closing" element={
                <ProtectedRoute allowedRoles={['admin', 'waiter']}>
                  <Layout>
                    <CashierClosingPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Nova rota para PDV */}
              <Route path="/pdv" element={
                <ProtectedRoute allowedRoles={['admin', 'waiter']}>
                  <Layout>
                    <PDVPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Rota da cozinha - apenas admin e cozinha */}
              <Route path="/kitchen" element={
                <ProtectedRoute allowedRoles={['admin', 'kitchen']}>
                  <Layout>
                    <KitchenPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Rota de entregas - apenas admin e entregadores */}
              <Route path="/delivery" element={
                <ProtectedRoute allowedRoles={['admin', 'delivery']}>
                  <Layout>
                    <DeliveryPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Rota de configurações */}
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SettingsPage />
                    </Layout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Rota de página não encontrada */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
